import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  cancelOperation,
  getOperationDetailsPdf,
  useOperationDetails,
  useOperationStatusIcons,
} from '@/shared/api';
import type { OperationDetailsResponse, OperationDto } from '@/shared/types';
import { useSessionStore } from '@/entities/session';
import { useConfirmationModal } from '@/widgets';
import { useAfterOperationRefresh } from './useAfterOperationRefresh';
import { useCancelOperationResult } from './useCancelOperationResult';

type Params = {
  operation: OperationDto;
  onStatusChange?: () => void;
  onCanceled?: () => void;
};

export const useOperationDetailsState = ({
  operation,
  onStatusChange,
  onCanceled,
}: Params) => {
  const { t } = useTranslation();
  const { session } = useSessionStore();
  const { goToSuccessScreen, goToErrorScreen } = useCancelOperationResult();
  const afterOperationRefresh = useAfterOperationRefresh();
  const { openModal, Modal: ConfirmationModal } = useConfirmationModal();
  const { operationsStatusIconsData, isOperationStatusIconsLoading } =
    useOperationStatusIcons();

  const eWallet = session?.additional?.eWallet;
  const operationId = operation.operaionid || '';

  const {
    data: details,
    isLoading: isDetailsLoading,
    error: detailsError,
  } = useOperationDetails<OperationDetailsResponse>({
    operationId,
    refetchInterval: 0,
  });

  const isCancelable = details?.isCancelable === 1;
  const isPrintable = details?.isPrintable === 1;

  const [isPdfLoading, setIsPdfLoading] = useState(false);

  useEffect(() => {
    if (!details) return;

    if (operation.status !== details.status.paramStatus) {
      onStatusChange?.();
    }
  }, [operation, details]);

  const statusIcon = useMemo(
    () =>
      operationsStatusIconsData?.value.find(
        icon => icon.status === details?.status.paramStatus
      )?.icon,
    [operationsStatusIconsData, details]
  );

  const downloadPdf = useCallback(async () => {
    if (!isPrintable || isPdfLoading) return;

    setIsPdfLoading(true);

    try {
      const pdf = await getOperationDetailsPdf<Blob>(operationId);
      const fileName = `OperationsDetails_${eWallet}.pdf`;
      const url = URL.createObjectURL(
        new File([pdf], fileName, { type: 'application/pdf' })
      );
      const link = document.createElement('a');

      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(
        (error instanceof Error && error.message) || t('common.unknownError')
      );
    } finally {
      setIsPdfLoading(false);
    }
  }, [isPrintable, isPdfLoading, operationId, eWallet, t]);

  const requestCancel = useCallback(() => {
    openModal({
      title: t('confirmationModal.cancelOperation.title'),
      description: t('confirmationModal.cancelOperation.description'),
      confirmText: t('confirmationModal.cancelOperation.confirmText'),
      cancelText: t('confirmationModal.cancelOperation.cancelText'),
      onConfirm: async () => {
        try {
          await cancelOperation(operation.operaionid);
          afterOperationRefresh();
          onCanceled?.();
          goToSuccessScreen();
        } catch {
          goToErrorScreen();
        }
      },
    });
  }, [
    openModal,
    t,
    operation.operaionid,
    afterOperationRefresh,
    onCanceled,
    goToSuccessScreen,
    goToErrorScreen,
  ]);

  return {
    details,
    isLoading: isDetailsLoading || isOperationStatusIconsLoading,
    error: detailsError,
    statusIcon,
    isCancelable,
    isPrintable,
    isPdfLoading,
    downloadPdf,
    requestCancel,
    ConfirmationModal,
  };
};
